import { Play } from "lucide-react";
import { dictionary, Language } from "../../../utils/dictionary";
import { AdTableRow } from "../../../services/meta/metaService";
import StatusBadge from "../StatusBadge";
import ThumbnailWithPlaceholder from "./ThumbnailWithPlaceholder";

export type CreativeMetric = "hook_rate" | "ctr" | "cost_per_result" | "spend";

interface CreativeCardProps {
  ad: AdTableRow;
  rank: number;
  metric: CreativeMetric;
  language: Language;
  onClick: () => void;
}

const metricLabelKey = (m: CreativeMetric): string => {
  switch (m) {
    case "hook_rate": return "performance-metric-hook-rate";
    case "ctr": return "performance-metric-ctr";
    case "cost_per_result": return "performance-metric-cpa";
    case "spend": return "performance-metric-spend";
  }
};

const formatMetric = (m: CreativeMetric, ad: AdTableRow): string => {
  switch (m) {
    case "hook_rate": return (ad.hook_rate * 100).toFixed(1) + "%";
    case "ctr": return ad.ctr.toFixed(2) + "%";
    case "cost_per_result":
      if (ad.cost_per_result === 0) return "—";
      return "$" + ad.cost_per_result.toFixed(2);
    case "spend": return "$" + ad.spend.toFixed(0);
  }
};

export const CreativeCard = ({ ad, rank, metric, language, onClick }: CreativeCardProps) => {
  const label = dictionary[metricLabelKey(metric)]?.[language] ?? metric;
  const value = formatMetric(metric, ad);
  const formatLabel = dictionary[ad.format === "video" ? "performance-format-video" : "performance-format-image"]?.[language] ?? ad.format;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left bg-white border border-gray-200 rounded-xl p-2.5 hover:border-fluxi/50 hover:shadow-[0_4px_16px_rgba(67,104,229,0.08)] transition-all"
    >
      <div className="relative mb-2">
        <ThumbnailWithPlaceholder
          src={ad.thumbnail_url}
          alt={ad.ad_name}
          format={ad.format}
          className="w-full rounded-lg"
          style={{ aspectRatio: "4 / 5" }}
          iconClass="w-7 h-7"
        />
        <span className="absolute top-1.5 left-1.5 inline-flex items-center justify-center h-5 px-1.5 rounded-full bg-white/95 text-[10px] font-bold text-gray-900">
          #{rank}
        </span>
        {ad.format === "video" && (
          <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/70">
            <Play className="w-2.5 h-2.5 text-white fill-white" />
          </span>
        )}
        <span className="absolute bottom-1.5 right-1.5">
          <StatusBadge status={ad.semaforo} language={language} compact />
        </span>
      </div>
      <p className="text-[12px] font-medium text-gray-900 truncate leading-tight">{ad.ad_name}</p>
      <div className="flex items-center justify-between gap-1 mt-1">
        <span className="text-[9px] font-semibold uppercase tracking-wide text-gray-400 truncate">{label}</span>
        <span className="text-[9px] text-gray-500 px-1 py-0.5 rounded bg-gray-100 flex-shrink-0">{formatLabel}</span>
      </div>
      <p className="text-[14px] font-semibold text-gray-900 tabular-nums leading-tight">{value}</p>
    </button>
  );
};

export default CreativeCard;
